import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type MutableRefObject } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../components/hotkeys';
import { BusyIcon } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { guardedExit, resetUnsavedTracking, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { TestParameterEditDialog } from './TestParameterEditDialog';
import './scientificDataTable.css';
import './parameterDefault.css';

const PAGE_ID = 'set-parameter-default';
const FORM_NAME = 'Parameter Default';
const BASE = '/api/v1/parameter-defaults';

type Tab = 'test' | 'qc';
/** In-grid editing of the Test Parameter tab (cell editors, Save changes / Cancel changes, Copy To
 *  All Cell). OFF since 2026-10-13 (owner decision): in legacy no save works on that grid, so G6
 *  shows it read-only as well. The edit code and the save endpoint stay; flip this to bring them back. */
const TEST_TAB_EDITABLE = false;
const TABS: { value: Tab; label: string }[] = [
  { value: 'test', label: 'Test Parameter' },
  { value: 'qc', label: 'QC Parameter' },
];

type Row = Record<string, unknown> & { oid: string; groupName: string };
type Kind = 'text' | 'number' | 'bool' | 'unit' | 'date';
interface Col { key: string; label: string; kind: Kind; width: number }

/** Legacy TestParameter_ListView_DefaultSettings — column order as the legacy grid. */
const TEST_COLS: Col[] = [
  { key: 'sort', label: 'Sort', kind: 'number', width: 80 },
  // Caption as the G5 grid header spells it.
  { key: 'surrogate', label: 'Surroagate', kind: 'bool', width: 95 },
  { key: 'internalStandard', label: 'InternalStandard', kind: 'bool', width: 130 },
  { key: 'defaultResult', label: 'DefaultResult', kind: 'text', width: 120 },
  { key: 'defaultUnits', label: 'DefaultUnits', kind: 'unit', width: 120 },
  { key: 'spikeAmount', label: 'SpikeAmount', kind: 'number', width: 115 },
  { key: 'spikeAmountUnit', label: 'SpikeAmountUnit', kind: 'unit', width: 135 },
  { key: 'finalDefaultResult', label: 'FinalDefaultResult', kind: 'text', width: 145 },
  { key: 'finalDefaultUnits', label: 'FinalDefaultUnits', kind: 'unit', width: 140 },
  { key: 'surrogateAmount', label: 'SurrogateAmount', kind: 'number', width: 135 },
  { key: 'surrogateUnits', label: 'SurrogateUnits', kind: 'unit', width: 125 },
  { key: 'surrogateLowLimit', label: 'SurrogateLowLimit', kind: 'text', width: 145 },
  { key: 'surrogateHighLimit', label: 'SurrogateHighLimit', kind: 'text', width: 150 },
  { key: 'loq', label: 'LOQ', kind: 'text', width: 80 },
  { key: 'uql', label: 'UQL', kind: 'text', width: 80 },
  { key: 'rptLimit', label: 'RptLimit', kind: 'text', width: 90 },
  { key: 'mdl', label: 'MDL', kind: 'text', width: 80 },
  { key: 'mcl', label: 'MCL', kind: 'text', width: 80 },
  { key: 'sigFig', label: 'SigFig', kind: 'text', width: 80 },
  { key: 'cutOff', label: 'CutOff', kind: 'text', width: 80 },
  { key: 'decimal', label: 'Decimal', kind: 'text', width: 85 },
  { key: 'comment', label: 'Comment', kind: 'text', width: 200 },
  { key: 'retireDate', label: 'RetireDate', kind: 'date', width: 110 },
];

/** Legacy TestParameter_ListView_DefaultSettings_QC — read-only. */
const QC_COLS: Col[] = [
  ...TEST_COLS.slice(0, 9),
  { key: 'stdConc', label: 'STDConc', kind: 'text', width: 95 },
  { key: 'stdConcUnit', label: 'STDConcUnit', kind: 'unit', width: 115 },
  { key: 'stdVolAdd', label: 'STDVolAdd', kind: 'text', width: 105 },
  { key: 'stdVolUnit', label: 'STDVolUnit', kind: 'unit', width: 110 },
  { key: 'recLCLimit', label: 'RecLCLimit', kind: 'text', width: 105 },
  { key: 'recHCLimit', label: 'RecHCLimit', kind: 'text', width: 105 },
  { key: 'rpdLCLimit', label: 'RPDLCLimit', kind: 'text', width: 105 },
  { key: 'rpdHCLimit', label: 'RPDHCLimit', kind: 'text', width: 105 },
  { key: 'lowCLimit', label: 'LowCLimit', kind: 'text', width: 100 },
  { key: 'highCLimit', label: 'HighCLimit', kind: 'text', width: 100 },
  { key: 'reLCLimit', label: 'RELCLimit', kind: 'text', width: 100 },
  { key: 'reHCLimit', label: 'REHCLimit', kind: 'text', width: 100 },
  ...TEST_COLS.slice(18),
];

/** A cell as the comparable text the save sends ("" = empty; ticks true/false; dates yyyy-MM-dd). */
const cellText = (v: unknown, kind: Kind): string => {
  if (kind === 'bool') return v === true || v === 'true' ? 'true' : 'false';
  if (v == null) return '';
  return kind === 'date' ? String(v).slice(0, 10) : String(v);
};

/**
 * Settings ▸ Parameter ▸ Parameter Default (legacy dashboard `TestParameterSettings`).
 *
 * Two tabs over the test parameters — **Test Parameter** (QC Type "Sample") and **QC Parameter**
 * (every other QC type) — each grouped by test under the legacy heading "Matrix · Test · Method ·
 * Component". The Test Parameter tab is edited in the grid and saved as one batch (Save
 * changes, Cancel changes; right-click a cell → Copy To All Cell); the QC Parameter tab is
 * read-only, as in legacy. There is no detail view in legacy and none here, which is why this is a
 * bespoke page built from the shared grid, toolbar, title and save flow.
 */
export function ParameterDefaultPage({ onHome }: { onHome: () => void }) {
  const [tab, setTab] = useState<Tab>('test');
  // A tab is mounted on its first visit and then kept, so its unsaved edits survive a look at the other.
  const [visited, setVisited] = useState<Set<Tab>>(new Set(['test']));
  const [meta, setMeta] = useState<{ loadMs: number | null; loading: boolean }>({ loadMs: null, loading: true });
  const onMeta = useCallback((loadMs: number | null, loading: boolean) => setMeta({ loadMs, loading }), []);
  const show = (next: Tab) => { setTab(next); setVisited((v) => (v.has(next) ? v : new Set(v).add(next))); };
  // The page's own actions live ABOVE the tabs, as legacy (owner 2026-10-13): Save stores the Test
  // Parameter tab's edits whichever tab is showing; Testparameter Edit opens the test picker.
  const testSave = useRef<(() => void) | null>(null);
  const [edits, setEdits] = useState<{ dirty: boolean; saving: boolean }>({ dirty: false, saving: false });
  const onEdits = useCallback((dirty: boolean, saving: boolean) => setEdits({ dirty, saving }), []);
  const [editOpen, setEditOpen] = useState(false);
  // Bumped after Testparameter Edit saves, so both grids reload with the new parameter set.
  const [reload, setReload] = useState(0);
  useHotkeys([
    { combo: 'mod+s', label: 'Save', group: 'List', order: 1, run: () => testSave.current?.(), disabled: !edits.dirty || edits.saving },
  ]);

  return (
    <div className="bl-page mlv-host">
      <div className="bl-grid-area">
        <div className="dp-breadcrumb">
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          {/* subgroup node (owner request 2026-10-14) */}
          <span>Parameter</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Parameter Default</span>
        </div>
        <FormTitle pageId={PAGE_ID} loadMs={meta.loadMs} loading={meta.loading} auditFormName={FORM_NAME} />
        <div className="pd-actions">
          <button type="button" className="ca-btn" onClick={() => setEditOpen(true)} title="Testparameter Edit">
            <i className="ti ti-list-check" /> Testparameter Edit
          </button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={() => testSave.current?.()}
            disabled={!edits.dirty || edits.saving} title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}>
            {edits.saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
        </div>
        <div className="sdt-bar" data-unsaved-ignore>
          <span className="sdt-tabs" role="tablist" aria-label="Parameter set">
            {TABS.map((t) => (
              <button key={t.value} type="button" role="tab" aria-selected={t.value === tab}
                className={t.value === tab ? 'on' : ''} onClick={() => show(t.value)}>{t.label}</button>
            ))}
          </span>
        </div>
        {TABS.filter((t) => visited.has(t.value)).map((t) => (
          <ParameterDefaultTab key={`${t.value}-${reload}`} tab={t.value} label={t.label} active={t.value === tab} onMeta={onMeta}
            saveRef={t.value === 'test' ? testSave : undefined} onEdits={t.value === 'test' ? onEdits : undefined} />
        ))}
        {editOpen && (
          <TestParameterEditDialog onClose={() => setEditOpen(false)} onSaved={() => setReload((n) => n + 1)} />
        )}
      </div>
    </div>
  );
}


function ParameterDefaultTab({ tab, label, active, onMeta, saveRef, onEdits }: {
  tab: Tab; label: string; active: boolean;
  /** Test Parameter tab only: where the page bar's Save finds this tab's save. */
  saveRef?: MutableRefObject<(() => void) | null>;
  /** Test Parameter tab only: tells the page bar whether there is anything to save. */
  onEdits?: (dirty: boolean, saving: boolean) => void;
  onMeta: (loadMs: number | null, loading: boolean) => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const isTest = tab === 'test';
  const editable = isTest && TEST_TAB_EDITABLE;
  const cols = isTest ? TEST_COLS : QC_COLS;

  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState<Map<string, Row>>(new Map());
  const [units, setUnits] = useState<string[]>([]);
  const [open, setOpen] = useState<Set<string | number>>(new Set());
  // G5 grid has a row checkbox per parameter (+ select-all); no action here acts on it.
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  // Right-click "Copy To All Cell": the cell it was opened on.
  const [menu, setMenu] = useState<{ x: number; y: number; oid: string; key: string } | null>(null);

  /** oid → the cells that differ from what was loaded. */
  const changed = useMemo(() => {
    const out = new Map<string, Record<string, string>>();
    if (!editable) return out;
    for (const r of rows) {
      const base = loaded.get(r.oid);
      if (!base) continue;
      for (const c of cols) {
        const now = cellText(r[c.key], c.kind);
        if (now !== cellText(base[c.key], c.kind)) out.set(r.oid, { ...(out.get(r.oid) ?? {}), [c.key]: now });
      }
    }
    return out;
  }, [rows, loaded, cols, editable]);
  const dirty = changed.size > 0;
  // Rule 15: the grid buffer is state the automatic tracker cannot see.
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: { rows?: Row[]; units?: string[] } }>(BASE, { params: { tab } });
      const list = (res.data?.data?.rows ?? []).map((r) => ({ ...r, oid: String(r.oid), groupName: String(r.groupName ?? '') }));
      setRows(list);
      setLoaded(new Map(list.map((r) => [r.oid, r])));
      setUnits(res.data?.data?.units ?? []);
      resetUnsavedTracking();
    } catch {
      notify.error('The parameter defaults could not be loaded. Check that the server is running, then press Refresh.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (active) onMeta(loadMs, loading); }, [active, loadMs, loading, onMeta]);

  const patch = (oid: string, key: string, value: unknown) =>
    setRows((d) => d.map((r) => (r.oid === oid ? { ...r, [key]: value } : r)));

  const save = () => {
    if (!dirty || saving) return;
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        setSaving(true);
        try {
          await api.post(`${BASE}/save`, { rows: [...changed].map(([oid, values]) => ({ oid, values })) });
        } finally {
          setSaving(false);
        }
      },
      onSaved: () => { void load(); },
    });
  };
  /** Cancel changes — back to what was loaded. */
  const cancel = () => { setRows((d) => d.map((r) => loaded.get(r.oid) ?? r)); resetUnsavedTracking(); };
  const refresh = () => { if (dirty) void guardedExit(() => { void load(); }); else void load(); };

  // The page bar's Save (and its Ctrl+S) reach this tab's save through the ref.
  useEffect(() => { if (saveRef) saveRef.current = save; });
  useEffect(() => { onEdits?.(dirty, saving); }, [onEdits, dirty, saving]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.groupName} ${String(r.parameter ?? '')} ${String(r.qcType ?? '')}`.toLowerCase().includes(q));
  }, [rows, search]);

  /** One heading row per test, as G5 groups the grid; its parameters are the rows of that group. */
  const groups = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of shown) m.set(r.groupName, [...(m.get(r.groupName) ?? []), r]);
    return [...m].map(([groupName, members]): Row & { members: Row[] } => ({ oid: `group:${groupName}`, groupName, members }));
  }, [shown]);
  // A parameter with no Component has no heading text in legacy: it sits under a bare "Child:".
  const groupLabel = (g: Row) =>
    `Child: ${g.groupName ? `${g.groupName} ` : ''}(Count=${(g as Row & { members: Row[] }).members.length})`;

  /** Copy To All Cell — the clicked cell's value into the same column of every row of its group. */
  const copyToAll = () => {
    if (!menu) return;
    const src = rows.find((r) => r.oid === menu.oid);
    if (src) setRows((d) => d.map((r) => (r.groupName === src.groupName ? { ...r, [menu.key]: src[menu.key] } : r)));
    setMenu(null);
  };

  const rowColumns: AppColumn<Row>[] = useMemo(() => {
    const onMenu = (key: string) => (editable
      ? (e: ReactMouseEvent<HTMLTableCellElement>, r: Row) => { e.preventDefault(); setMenu({ x: e.clientX, y: e.clientY, oid: r.oid, key }); }
      : undefined);
    const lead: AppColumn<Row>[] = [
      ...(isTest ? [] : [{ field: 'qcType', header: 'QCType', width: 110 } as AppColumn<Row>]),
      { field: 'parameter', header: 'Parameter', width: 220 },
    ];
    return [...lead, ...cols.map((c): AppColumn<Row> => {
      if (c.kind === 'bool') {
        return {
          field: c.key, header: c.label, width: c.width, align: 'center', onCellContextMenu: onMenu(c.key),
          value: (r) => (cellText(r[c.key], 'bool') === 'true' ? 'Yes' : 'No'),
          text: (r) => (cellText(r[c.key], 'bool') === 'true' ? 'Yes' : 'No'),
          render: (r) => (
            <input type="checkbox" className="sdt-check" disabled={!editable}
              aria-label={`${c.label} — ${String(r.parameter ?? '')}`}
              checked={cellText(r[c.key], 'bool') === 'true'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => patch(r.oid, c.key, e.target.checked)} />
          ),
        };
      }
      return {
        field: c.key, header: c.label, width: c.width, onCellContextMenu: onMenu(c.key),
        type: c.kind === 'date' ? 'date' : c.kind === 'number' ? 'number' : 'text',
        align: c.kind === 'number' ? 'right' : undefined,
        edit: !editable ? undefined
          : c.kind === 'unit' ? { type: 'combo', options: units, clearable: true }
          : { type: c.kind === 'date' ? 'date' : c.kind === 'number' ? 'number' : 'text' },
      };
    })];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cols, editable, isTest, units]);

  // G5: the grid header names the PARAMETER columns and the group rows sit inside the same table;
  // sorting groups by a parameter column means nothing, so the headers do not sort.
  const groupedColumns: AppColumn<Row>[] = useMemo(
    () => rowColumns.map((c) => ({ ...c, sortable: false })), [rowColumns]);

  const exportColumns: AppColumn<Row>[] = useMemo(
    () => [{ field: 'groupName', header: 'Child' }, ...rowColumns], [rowColumns]);

  // The tab's own bar holds Refresh · Export to only; Save sits on the page bar above the tabs, and
  // Save changes / Cancel changes under the grid, as legacy.
  const saveButton = (labelText: string) => (
    <button className="ca-btn ca-btn-solid" onClick={save} disabled={!dirty || saving}
      title={`${labelText} (${formatCombo(effectiveCombo('mod+s'))})`}>
      {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} {labelText}
    </button>
  );

  return (
    <div className="sdt-panel" role="tabpanel" aria-label={label} hidden={!active}>
      <AppToolbar
        noHotkeys={!active}
        showMeta={active}
        onRefresh={refresh}
        refreshing={loading}
        exportData={{ filename: `Parameter Default - ${label}`, ...gridExportMatrix(shown, exportColumns) }}
        right={(
          <span className="sdt-search" data-unsaved-ignore>
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search…" aria-label="Search" value={search}
                onChange={(e) => setSearch(e.target.value)} />
              {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
            </div>
            {dirty && <span className="sdt-pending">{changed.size} unsaved {changed.size === 1 ? 'row' : 'rows'}</span>}
          </span>
        )}
      />

      <AppDataGrid<Row>
        rows={groups}
        columns={groupedColumns}
        getRowId={(g) => g.oid}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        loading={loading}
        defaultPageSize={20}
        hideHeaderTools
        showFilters={false}
        onRowClick={(g) => setOpen((s) => { const n = new Set(s); if (n.has(g.oid)) n.delete(g.oid); else n.add(g.oid); return n; })}
        emptyText="No parameter defaults"
        rowDetail={{
          expandedIds: open,
          onExpandedChange: setOpen,
          expandTitle: 'Show parameters',
          // G5 has no expand / collapse-all button in that header cell (owner 2026-10-14).
          hideExpandAll: true,
          // G5 grouped grid: the heading spans the row, and an open group's parameters follow as
          // rows of the SAME table under its parameter columns (not a nested grid).
          groupLabel: groupLabel,
          childRows: (g) => (g as Row & { members: Row[] }).members,
        }}
      />

      {editable && (
        <div className="pd-batch">
          {saveButton('Save changes')}
          <button className="ca-btn" onClick={cancel} disabled={!dirty || saving}>
            <i className="ti ti-arrow-back-up" /> Cancel changes
          </button>
        </div>
      )}

      {menu && (
        <>
          <div className="pd-menu-backdrop" onClick={() => setMenu(null)} onContextMenu={(e) => { e.preventDefault(); setMenu(null); }} />
          <div className="pd-menu" role="menu" style={{ left: menu.x, top: menu.y }}>
            <button type="button" role="menuitem" autoFocus onClick={copyToAll}>
              <i className="ti ti-copy" /> Copy To All Cell
            </button>
          </div>
        </>
      )}
    </div>
  );
}
